import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

const SEOHead = ({
    title = "Yunlen - Global Finance, Simplified",
    description = "Experience next-generation financial services with Yunlen. Virtual cards, multi-currency accounts, global transfers, crypto wallets, and stablecoin trading - all in one secure platform.",
    keywords = "Yunlen, fintech, virtual cards, multi-currency accounts, global transfers, crypto wallets, stablecoin trading, digital banking, financial services, mobile money, international transfers",
    canonicalUrl = "https://yunlen.com",
    ogImage = "/ic_launcher.png",
    ogType = "website",
    twitterCard = "summary_large_image",
    structuredData = null,
    noIndex = false,
    ogImageWidth = null,
    ogImageHeight = null
}) => {
    useEffect(() => {
        document.title = title;
    }, [title]);

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content="Yunlen" />
            <link rel="canonical" href={canonicalUrl} />

            {/* Robots */}
            {noIndex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            {ogImageWidth ? <meta property="og:image:width" content={String(ogImageWidth)} /> : null}
            {ogImageHeight ? <meta property="og:image:height" content={String(ogImageHeight)} /> : null}
            <meta property="og:site_name" content="Yunlen" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter / X reads name=, not property= */}
            <meta name="twitter:card" content={twitterCard} />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:site" content="@yunlenapp" />

            {/* Additional SEO Meta Tags */}
            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
            <meta name="theme-color" content="#10B981" />
            <meta name="msapplication-TileColor" content="#10B981" />
            <meta name="msapplication-TileImage" content="/icon-192.png" />

            {/* Icons: the round navy mark, as on Yunlen Business */}
            <link rel="icon" type="image/x-icon" href="/favicon.ico" />
            <link rel="icon" type="image/png" sizes="192x192" href="/icon-192.png" />
            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

            {/* PWA Manifest */}
            <link rel="manifest" href="/manifest.json" />

            {/* Preconnect to external domains for performance */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="preconnect" href="https://www.googletagmanager.com" />

            {/* Security Headers */}
            <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
            <meta httpEquiv="X-Frame-Options" content="SAMEORIGIN" />
            <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
            <meta name="referrer" content="strict-origin-when-cross-origin" />

            {/* Structured Data */}
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEOHead;
